import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-notes-preview/artifacts/qfix72o2_Lumero_Logo_transparent_with_name-removebg-preview.png";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/app/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="LumeroFlow" className="h-9 mx-auto mb-6" />
          </Link>
          <h1 className="text-2xl font-medium text-[#1A1B1A] mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Welcome back
          </h1>
          <p className="text-sm text-[#757675]">Sign in to your clinic workspace</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl" data-testid="login-error">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[#1A1B1A] mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email-input"
              className="w-full px-4 py-3 rounded-xl border border-[#1A1B1A]/10 bg-white text-sm text-[#1A1B1A] placeholder-[#757675] focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A] transition-colors"
              placeholder="you@clinic.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1A1B1A] mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="w-full px-4 py-3 rounded-xl border border-[#1A1B1A]/10 bg-white text-sm text-[#1A1B1A] placeholder-[#757675] focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A] transition-colors pr-10"
                placeholder="Enter password"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#757675]">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="flex justify-end">
            <Link to="/app/forgot-password" className="text-xs text-[#205B5A] hover:text-[#174544]">Forgot password?</Link>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full bg-[#205B5A] text-white py-3 rounded-full font-medium text-sm hover:bg-[#174544] transition-colors disabled:opacity-50"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <p className="text-center text-sm text-[#757675] mt-6">
          Don't have an account? <Link to="/app/signup" className="text-[#205B5A] font-medium hover:text-[#174544]" data-testid="goto-signup-link">Sign up</Link>
        </p>
        <p className="text-center mt-4">
          <Link to="/" className="text-xs text-[#757675] hover:text-[#4A4B4A]">Back to website</Link>
        </p>
      </div>
    </div>
  );
}
